import { Attachment, FlatButton, Icon, Label } from '@globalmed/atlas-ui';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FormBuilderPreviewFile.module.css';
import { UploadFile } from '@/components/UploadFile';
import {
  TFormBuilderFileField,
  TUploadDocumentDataRequestCamel,
} from '@/types';
import { convertFileToUploadData } from '@/utils/convertToFile';
import { saveBlob } from '@/utils/downloadFile';
import { formatFileSize } from '@/utils/fileFormat';

type TProps = {
  field: TFormBuilderFileField;
};

/**
 * Preview component for file upload field in form builder
 * @param props - Component props
 * @param props.field - File field data to preview
 * @returns Form builder file preview component
 */
export const FormBuilderPreviewFile = ({ field }: TProps) => {
  const { t } = useTranslation();
  const fileInputId = useId();
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [files, setFiles] = useState<TUploadDocumentDataRequestCamel[]>([]);

  const handleFileUpload = useCallback(async (files: File[]) => {
    setIsFileLoading(true);
    try {
      const uploadedFiles = await Promise.all(
        files.map((file) => convertFileToUploadData(file)),
      );
      setFiles(uploadedFiles);
    } catch (error) {
      console.error('Failed to upload files:', error);
    } finally {
      setIsFileLoading(false);
    }
  }, []);

  const handleFileClick = async (uri: string, fileName: string) => {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();

      await saveBlob(
        blob,
        fileName,
        blob.type || 'application/octet-stream',
        () => {
          const blobUrl = URL.createObjectURL(blob);
          const newWindow = window.open(blobUrl, '_blank');

          if (newWindow) {
            newWindow.onload = () => {
              URL.revokeObjectURL(blobUrl);
            };
          }
        },
      );
    } catch (error) {
      console.error('Failed to open file:', error);
    }
  };

  const handleRemoveFile = (id: string) =>
    setFiles((pr) => pr.filter((f) => f.id !== id));

  return (
    <div className={styles.container}>
      <Label
        label={field.label}
        required={field.required}
        htmlFor={fileInputId}
      />

      <UploadFile
        id={fileInputId}
        setFiles={handleFileUpload}
        className={styles.file}
        disabled={isFileLoading}
      />

      {files?.map((file) => (
        <div key={file.id} className={styles.fileRow}>
          <div className={styles.attachment}>
            <Attachment
              fileName={file.name}
              date={file.createdAt}
              fileSize={formatFileSize(file.size)}
              size="sm"
              sx={{ width: 'calc(100% - 20px)' }}
              onClick={() => handleFileClick(file.uri, file.name)}
              previewType="image"
              image={file.uri}
              aria-label={t('common.open-file', { name: file.name })}
            />
          </div>

          <FlatButton
            aria-label={t('common.delete-file', { name: file.name })}
            onClick={() => handleRemoveFile(file.id)}
          >
            <Icon name="Trash" color="var(--colors-error700)" decorative />
          </FlatButton>
        </div>
      ))}
    </div>
  );
};
